/**
 * The bench: what people do while they are not on court (decisions 28 and 33). Heckles for the
 * wall, one curse each (a pair plays wrong handed, without smashes, or one of them alone, for a
 * minute), a bounty on the main board's top three, free picks of who wins each court, naming
 * rights after a thrashing. Everything that moves fun points is derived from the log here, so a
 * corrected score settles its picks and bounties again by itself. Nothing touches the main board.
 */

import { makeRng } from "./rng";
import { eventById, gameResult } from "./scoring";
import type { Curse, CurseKind, GameRecord, Lineup, Pair, Pick, PlayerId, Round, TournamentState, Twist } from "./types";

/** Why a curse is refused while an admin has curses switched off. */
export const CURSES_OFF = "curses are off for now";

/** The curses, in the order a phone offers them. */
export const CURSE_KINDS: readonly CurseKind[] = ["wrong-hand", "no-smashes", "solo"];

/** What each curse is called, everywhere it is named. */
export const CURSE_NAMES: Record<CurseKind, string> = {
  "wrong-hand": "Wrong hand",
  "no-smashes": "No smashes",
  solo: "Solo",
};

/** Why a curse nobody has heard of is refused. */
export const NO_SUCH_CURSE = "there is no such curse";

export function isCurseKind(kind: unknown): kind is CurseKind {
  return typeof kind === "string" && (CURSE_KINDS as readonly string[]).includes(kind);
}

/** Which curse it is: one thrown before there was a choice was wrong hand. */
export function curseKind(curse: { kind?: CurseKind }): CurseKind {
  return curse.kind ?? "wrong-hand";
}

/** The curse's name: "Wrong hand", "No smashes" or "Solo". */
export function curseName(curse: { kind?: CurseKind }): string {
  return CURSE_NAMES[curseKind(curse)];
}

/** One line under each choice on the phone. */
export function curseLine(kind: CurseKind, seconds: number): string {
  if (kind === "no-smashes") return `No smashes for ${seconds} seconds`;
  if (kind === "solo") return `One of them, picked at random, plays alone for ${seconds} seconds`;
  return `Wrong handed for ${seconds} seconds`;
}

/**
 * Solo: which of the pair steps off. Drawn from the tournament's seed, the round, the court and
 * how many curses the day holds so far, so a replay draws the same player and no phone can choose.
 */
export function soloStepsOff(state: TournamentState, roundNo: number, court: number, pair: readonly PlayerId[]): PlayerId {
  const seed = (state.seed ^ Math.imul(roundNo + 1, 0x9e3779b1) ^ Math.imul(court + 1, 0x85ebca6b) ^ Math.imul(state.bench.curses.length + 1, 0xc2b2ae35)) >>> 0;
  return pair[makeRng(seed).int(pair.length)]!;
}

/**
 * The written heckles. `{court}` becomes "court 3", or "everyone" when aimed at the hall. The log
 * holds a line's place in this list, so never reorder or remove one: add at the end.
 */
export const HECKLES: string[] = [
  "My nan serves harder than {court}.",
  "Is {court} playing in slow motion?",
  "{court}, the net is the tall thing in the middle.",
  "Somebody on {court} is carrying somebody.",
  "{court} could use a fifth player.",
  "I have seen better footwork in a queue.",
  "{court}, that was in. Probably.",
  "Less chatting, more hitting, {court}.",
  "Whoever is serving on {court}: higher.",
  "{court} is a rally of two shots, tops.",
  "Fair play to {court}, they are trying.",
  "{court}, swap rackets, it cannot hurt.",
  "Put {court} on the highlights. The funny ones.",
  "The shuttle is the white thing, {court}.",
  "{court}, we can all see the score.",
];

/**
 * The same lines aimed at the whole hall, where "everyone" in place of a court reads badly. Null
 * keeps the line with "everyone". Same length and order as HECKLES.
 */
export const HECKLES_HALL: (string | null)[] = [
  "My nan serves harder than this lot.",
  null,
  null,
  "Somebody out there is carrying somebody.",
  "Every court could use a fifth player.",
  null,
  null,
  null,
  "Whoever is serving: higher.",
  "Every rally out there is two shots, tops.",
  "Fair play to everyone. Most of you are trying.",
  null,
  "Put this lot on the highlights. The funny ones.",
  null,
  null,
];

export function heckleText(line: number, court: number | null): string | null {
  const template = HECKLES[line];
  if (!template) return null;
  if (court === null && HECKLES_HALL[line]) return HECKLES_HALL[line]!;
  const who = court === null ? "everyone" : `court ${court}`;
  const text = template.replace("{court}", who);
  return text[0]!.toUpperCase() + text.slice(1);
}

function gameOf(round: Round | undefined, court: number): GameRecord | undefined {
  return round?.games.find((g) => g.court === court);
}

function samePair(x: readonly PlayerId[], y: readonly PlayerId[]): boolean {
  return (x[0] === y[0] && x[1] === y[1]) || (x[0] === y[1] && x[1] === y[0]);
}

/** The time the round's clocks are at: frozen while everything is paused. */
export function clockNow(state: TournamentState, now: number): number {
  return state.pausedAll && state.clock.pausedAt !== null ? state.clock.pausedAt : now;
}

function roundPlaying(state: TournamentState): Round | null {
  if (state.clock.phase !== "playing") return null;
  return state.rounds.find((r) => r.no === state.clock.roundNo) ?? null;
}

export interface TwistNow extends Twist {
  name: string;
  /** False while it is still to come. */
  on: boolean;
  /** Until it ends (or, while it is to come, until it starts). */
  leftMs: number;
}

/** The twist of the round being played: to come or running. Null once it is over. */
export function twistNow(state: TournamentState, now: number): TwistNow | null {
  const twist = roundPlaying(state)?.twist;
  if (!twist) return null;
  const t = clockNow(state, now);
  if (t >= twist.endsAt) return null;
  const on = t >= twist.startsAt;
  const name = eventById(state.config, twist.eventId)?.name ?? "Twist";
  return { ...twist, name, on, leftMs: (on ? twist.endsAt : twist.startsAt) - t };
}

/** The curses running right now. */
export function cursesNow(state: TournamentState, now: number): (Curse & { leftMs: number })[] {
  const round = roundPlaying(state);
  if (!round) return [];
  const t = clockNow(state, now);
  return (state.bench.curses ?? [])
    .filter((c) => c.roundNo === round.no && t >= c.startsAt && t < c.endsAt)
    .map((c) => ({ ...c, leftMs: c.endsAt - t }));
}

/** Why `id` cannot throw a curse right now, in plain words; null when they can. */
export function curseProblem(state: TournamentState, id: PlayerId, now: number): string | null {
  const p = state.players[id];
  if (!p) return "no such player";
  if (state.bench.cursesOff) return CURSES_OFF;
  if (p.cursesLeft < 1) return "you have thrown your curse";
  const round = roundPlaying(state);
  if (!round) return "curses are for a round being played";
  if (state.pausedAll) return "everything is paused";
  if (round.lineup.courts.some((c) => c.a.includes(id) || c.b.includes(id))) return "you are on court; curse when you are resting";
  const ends = state.clock.phaseEndsAt;
  if (ends === null || ends - now < state.config.bench.curseMs) return "too late in the round for a curse";
  if (twistNow(state, now)?.on) return "the whole hall is already under a twist";
  return null;
}

export interface BenchPoints {
  /** Won from bounties. */
  bounty: number;
  /** How many bounties they beat. */
  bountiesBeaten: number;
  /** Fun points from right picks. */
  picks: number;
  right: number;
  wrong: number;
  /** Right picks in a row, as it stands. */
  streak: number;
  bestStreak: number;
}

/** Which pair a pick backs in a game, or null if that pair is not on that court. */
export function pickSide(game: { a: Pair; b: Pair } | undefined, pick: Pick): "a" | "b" | null {
  if (!game) return null;
  if (samePair(game.a, pick.pair)) return "a";
  if (samePair(game.b, pick.pair)) return "b";
  return null;
}

/** What a pick did: true right, false wrong, null not settled (owed, voided, or the line-up changed). */
export function pickOutcome(state: TournamentState, pick: Pick): boolean | null {
  const game = gameOf(state.rounds[pick.roundNo - 1], pick.court);
  const side = pickSide(game, pick);
  if (!game || !side) return null;
  const result = gameResult(game, state.config);
  if (!result) return null;
  return result[side].won;
}

/** Each player's fun points from the bench. */
export function benchPoints(state: TournamentState): Map<PlayerId, BenchPoints> {
  const out = new Map<PlayerId, BenchPoints>();
  const row = (id: PlayerId): BenchPoints => {
    let r = out.get(id);
    if (!r) out.set(id, (r = { bounty: 0, bountiesBeaten: 0, picks: 0, right: 0, wrong: 0, streak: 0, bestStreak: 0 }));
    return r;
  };
  for (const round of state.rounds) {
    for (const b of round.bounties ?? []) {
      const game = round.games.find((g) => g.a.includes(b.player) || g.b.includes(b.player));
      const result = game ? gameResult(game, state.config) : null;
      if (!game || !result) continue;
      const [mine, theirs] = game.a.includes(b.player) ? [result.a, result.b] : [result.b, result.a];
      if (mine.won) continue;
      for (const id of theirs.players) {
        row(id).bounty += b.value;
        row(id).bountiesBeaten += 1;
      }
    }
  }
  // picks settle in the order the games were played: by round, then by court
  const from = state.config.bench.pickStreakFrom ?? 3;
  const picks = [...(state.bench.picks ?? [])].sort((x, y) => x.roundNo - y.roundNo || x.court - y.court);
  for (const pick of picks) {
    const outcome = pickOutcome(state, pick);
    if (outcome === null) continue;
    const r = row(pick.by);
    if (outcome) {
      r.right += 1;
      r.streak += 1;
      r.bestStreak = Math.max(r.bestStreak, r.streak);
      r.picks += r.streak >= from ? 2 : 1;
    } else {
      r.wrong += 1;
      r.streak = 0;
    }
  }
  return out;
}

/** The bounty on a court this round, summed per pair: what the other pair wins by beating them. */
export function bountiesOn(round: Round, court: number): { a: number; b: number } {
  const game = gameOf(round, court);
  const out = { a: 0, b: 0 };
  if (!game) return out;
  for (const b of round.bounties ?? []) {
    if (game.a.includes(b.player)) out.a += b.value;
    else if (game.b.includes(b.player)) out.b += b.value;
  }
  return out;
}

/** The line-up picks for a round are made on: the round itself, or the preview of the next one. */
export function pickLineup(state: TournamentState, roundNo: number): Lineup | null {
  const round = state.rounds[roundNo - 1];
  if (round) return round.lineup;
  return roundNo === state.rounds.length + 1 ? state.draft : null;
}

/** A player's picks for a round, as sides of the line-up as it stands (a pick on a redrawn pair is not shown). */
export function picksOf(state: TournamentState, id: PlayerId, roundNo: number): { court: number; side: "a" | "b" }[] {
  const lineup = pickLineup(state, roundNo);
  if (!lineup) return [];
  const out: { court: number; side: "a" | "b" }[] = [];
  for (const pick of state.bench.picks ?? []) {
    if (pick.by !== id || pick.roundNo !== roundNo) continue;
    const side = pickSide(lineup.courts.find((c) => c.court === pick.court), pick);
    if (side) out.push({ court: pick.court, side });
  }
  return out;
}

/** How the hall has picked a round: backers per pair, per court. For the wall. */
export function pickTally(state: TournamentState, roundNo: number): Map<number, { a: number; b: number }> {
  const out = new Map<number, { a: number; b: number }>();
  const lineup = pickLineup(state, roundNo);
  if (!lineup) return out;
  for (const pick of state.bench.picks ?? []) {
    if (pick.roundNo !== roundNo) continue;
    const side = pickSide(lineup.courts.find((c) => c.court === pick.court), pick);
    if (!side) continue;
    const row = out.get(pick.court) ?? { a: 0, b: 0 };
    row[side] += 1;
    out.set(pick.court, row);
  }
  return out;
}

/** Why a round cannot be picked right now, in plain words; null while it can. Picks close at half time. */
export function pickProblem(state: TournamentState, roundNo: number, now: number): string | null {
  if (roundNo === state.rounds.length + 1) return state.draft ? null : "the next round is not drawn yet";
  if (state.clock.phase !== "playing" || state.clock.roundNo !== roundNo) return "that round is over";
  if (state.pausedAll) return "everything is paused";
  const ends = state.clock.phaseEndsAt;
  if (ends === null || ends - now < state.config.gameMs / 2) return "too late: picks close at half time";
  return null;
}

/**
 * The games where `winner` holds a naming right they have not used: won by the margin, in the
 * round being played or the one before it. Newest first.
 */
export function namingRights(state: TournamentState, winner: PlayerId): { roundNo: number; court: number; losers: PlayerId[] }[] {
  const out: { roundNo: number; court: number; losers: PlayerId[] }[] = [];
  const latest = state.rounds.length;
  for (const round of state.rounds.slice(-2).reverse()) {
    if (round.no < latest - 1) continue;
    for (const game of round.games) {
      if (game.dubUsed || game.voided || !game.score) continue;
      const onA = game.a.includes(winner);
      const onB = game.b.includes(winner);
      if (!onA && !onB) continue;
      const margin = onA ? game.score.a - game.score.b : game.score.b - game.score.a;
      if (margin < state.config.bench.dubMargin) continue;
      out.push({ roundNo: round.no, court: game.court, losers: [...(onA ? game.b : game.a)] });
    }
  }
  return out;
}
